/* eslint-disable arrow-body-style */
import * as React from "react";
import {Component} from "react";
import {observer} from "mobx-react";

import "./document-form.scss";

import {Icon} from "../../core/components/icon/icon";
import {Button, Form, Input, Message, Progress} from "semantic-ui-react";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";
import {AppState} from "../../components/app/app-mobx";

import {InputOnChangeData} from "semantic-ui-react/dist/commonjs/elements/Input/Input";
import {Project} from "../../models/project";
import {Document} from "../../models/document";
import moment from "moment/moment";

export enum StatusType {
    Saving = "saving",
    Warning = "warning",
    Complete = "successful",
    Error = "error"
}

export interface DocumentFormProps {
    appStore: AppState;
    auftragsverwaltung: Auftragsverwaltung;
    project: Project;
    onCancel?: (e: React.MouseEvent) => void;
    onSave?: (document: Document) => void;
}

@observer
export class DocumentForm extends Component<DocumentFormProps, any> {
    watchHandles: any[] = [];

    constructor(props: DocumentFormProps) {
        super(props);
        this.state = {
            formError: false,
            saveError: null,
            saveState: null,

            fileName: null,
            label: null,
            date: null,
            fileSize: null,
            labelError: false,
            fileNameError: false,
            dateError: false,
            fileSizeError: false
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    componentDidMount(): void {
        const {auftragsverwaltung} = this.props;
        const document = auftragsverwaltung.selectedDocument;

        if (!document) {
            return;
        }

        // pass partial order properties to state. Use controlled state with form controls
        this.setState({
            fileName: document.fileName,
            label: document.label,
            date: document.date,
            fileSize: document.fileSize
        });
    }

    render(): React.ReactElement | null {
        const {auftragsverwaltung} = this.props;
        const document = auftragsverwaltung.selectedDocument;

        const {
            label,
            fileName,
            date,
            fileSize,
            labelError,
            fileNameError,
            dateError,
            fileSizeError,
            saveError,
            saveState
        } = this.state;

        if (!document) {
            return null;
        }

        let progress: React.ReactElement | null = null;
        if (saveState) {
            progress = (
                <Progress
                    percent={saveState === StatusType.Complete ? 100 : 50}
                    attached="bottom"
                    active={saveState === StatusType.Saving}
                    warning={saveState === StatusType.Warning}
                    error={saveState === StatusType.Error}
                    success={saveState === StatusType.Complete}
                />
            );
        }

        return (
            <div className="document-form">
                <Form size="huge" onSubmit={(e) => {
                    e.preventDefault();
                    this.handleSave(document);
                }}>
                    <Form.Group widths="equal">
                        <Form.Field
                            id="form-input-control-label"
                            name="label"
                            control={Input}
                            label={{ children: "Anzeigename", htmlFor: "form-input-control-label" }}
                            placeholder="z.B. Ersterhebung GK Luthern Wiggertal"
                            value={label}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                                this.handleChange(event, data);
                            }}
                            error={labelError?
                                {
                                    content: "Bitte gültigen Anzeigename eingeben",
                                } : null
                            }
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field
                            id="form-input-control-file-name"
                            name="fileName"
                            control={Input}
                            label={{ children: "Dateiname", htmlFor: "form-input-control-file-name" }}
                            placeholder="z.B. 2100_luth (ohne Dateiendung)"
                            value={fileName}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                                this.handleChange(event, data);
                            }}
                            error={fileNameError?
                                {
                                    content: "Bitte gültigen Dateinamen eingeben",
                                } : null
                            }
                        />
                        <Form.Input
                            id="form-input-control-date"
                            name="date"
                            type="date"
                            label={{children: "Datum Dokument", htmlFor: "form-input-control-date" }}
                            width={4}
                            value={date ? moment(date).format("YYYY-MM-DD") : "-"}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                                this.handleChange(event, data);
                            }}
                            error={dateError?
                                {
                                    content: "Bitte gültiges Datum des Dokuments eingeben",
                                } : null
                            }
                        />
                        <Form.Field
                            id="form-input-control-file-size"
                            name="fileSize"
                            control={Input}
                            label={{ children: "Dateigrösse", htmlFor: "form-input-control-file-size" }}
                            width={5}
                            placeholder="z.B. 17 (in MB ohne Einheit)"
                            value={fileSize}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                                this.handleChange(event, data);
                            }}
                            error={fileSizeError?
                                {
                                    content: "Bitte gültige Dateigrösse eingeben",
                                } : null
                            }
                        />
                    </Form.Group>
                    {progress}
                    {saveState && saveState === StatusType.Complete &&
                        <Message attached="bottom" positive>
                            <Icon name={"data_floppy_disk"} />
                            Erfolgreich gespeichert!
                        </Message>
                    }
                    {saveState && saveState === StatusType.Error &&
                        <Message attached="bottom" negative>
                            <Icon name={"error"} />
                            Fehler beim Speichern! {saveError}
                        </Message>
                    }
                    <Button className={"save"} primary type="submit">
                        <Icon name={"floppy_disk"} />
                        Speichern
                    </Button>

                    <Button className={"cancel"} secondary
                        onClick={(e: React.MouseEvent) => {
                            if (this.props.onCancel) {
                                this.props.onCancel(e);
                            }
                        }}>
                        <Icon name={"navigate_cross"} />
                        Abbrechen
                    </Button>
                </Form>
            </div>
        );
    }

    handleChange(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData): void {
        let value: string | Date = data.value;
        if (data.name === "date") {
            value = new Date(`${data.value} 12:00:00`);
        }
        this.setState({[data.name]: value});
    };

    handleSave(document: Document): void {
        const {appStore, auftragsverwaltung, project, onSave} = this.props;
        const {label, fileName, date, fileSize} = this.state;

        const hasError = this.validateFormInput();
        if (hasError) {
            this.setState({formError: true});
            return;
        }

        // make progress indicator visible
        this.setState({
            formError: false,
            saveState: StatusType.Saving
        });

        const fileDate = date ? date.getTime() : null;

        const updated = {
            projectId: project.id,
            label,
            fileName,
            date: fileDate,
            fileSize
        };

        const isNewRecord = document?.isNewRecord;

        const user = appStore.esriCredential?.userId;
        auftragsverwaltung.DocumentStore.save(document, updated, user || "")
            .then((doc: Document) => {
                this.setState({
                    saveState: StatusType.Complete,
                    saveError: null
                });
                if (isNewRecord) {
                    // hide form when a new record was added
                    auftragsverwaltung.setEditing(false);
                }
                if (onSave) {
                    onSave(doc);
                }
            }).catch((error) => {
                this.setState({
                    saveState: StatusType.Error,
                    saveError: error.message
                });
            });
    }

    private validateFormInput(): boolean {
        const {label, fileName, date, fileSize,} = this.state;

        const validates = {
            labelError: false,
            fileNameError: false,
            dateError: false,
            fileSizeError: false
        };
        let isError: boolean = false;

        if (!label) {
            validates.labelError = true;
            isError = true;
        }
        if (!fileName) {
            validates.fileNameError = true;
            isError = true;
        }
        if (!date) {
            validates.dateError = true;
            isError = true;
        }
        if (!fileSize) {
            validates.fileSizeError = true;
            isError = true;
        }

        this.setState(validates);
        return isError;
    }

}
